import React, { useState } from 'react';

/**
 * Contact component with card-based layout and floating form
 */
const Contact = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    query: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    return formData.name.trim() &&
           formData.email.trim() &&
           formData.phone.trim() &&
           formData.query.trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Please fill in all fields before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      // The webhook expects a GET request with query parameters, not POST.
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => params.append(k, v));
      const url = `https://n8n.aakeeocrm.com/webhook/5fed7f08-2230-4d07-a78b-7db4c26c0246?${params.toString()}`;
      const response = await fetch(url, { method: 'GET' });

      // Helpful diagnostics: some failures are caused by CORS/preflight or server errors.
      // Log response details for debugging.
      if (response.type === 'opaque') {
        // Opaque response usually means the request was made with mode:'no-cors' or blocked by CORS.
        console.warn('Opaque response from webhook (possible CORS issue).', response);
        alert('Network error while sending message (possible CORS restriction). Please try again or contact us directly.');
      } else if (response.ok) {
        alert('Thanks for reaching out - an expert will contact you within one business day.');
        setFormData({ name: '', email: '', phone: '', query: '' });
        setShowForm(false);
      } else {
        const text = await response.text().catch(() => '<no body>');
        console.error('Webhook returned non-OK status', response.status, text);
        alert(`Something went wrong (server responded ${response.status}). Please try again or contact us directly.`);
      }
    } catch (error) {
      // Network or CORS error will be caught here. Browser console will show more.
      console.error('Form submission error:', error);
      alert('Network error while sending message. Check console or try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openForm = () => setShowForm(true);
  const closeForm = () => setShowForm(false);

  return (
    <>
      <section id="contact" className="section contact reveal">
        <div className="section-header">
          <h2>Contact Options</h2>
          <p>Ready to improve your business processes? Choose how you'd like to get started.</p>
        </div>

        <div className="contact-cards-grid">

          {/* Email Contact Card */}
          <div className="contact-card">
            <div className="contact-card-icon">
              <i className="fa-solid fa-envelope"></i>
            </div>
            <h3>Email Us</h3>
            <p>Send us an email and we'll get back to you within one business day.</p>
            <a href="mailto:sales@aakeeo.com" className="contact-card-link">
              sales@aakeeo.com
            </a>
          </div>

          {/* Call/WhatsApp Card */}
          <div className="contact-card">
            <div className="contact-card-icon">
              <i className="fa-solid fa-phone"></i>
            </div>
            <h3>Call or WhatsApp</h3>
            <p>Speak directly with our team for immediate assistance and quick answers.</p>
            <div className="contact-card-buttons">
              <a href="tel:+918431748754" className="contact-btn call-btn">
                <i className="fa-solid fa-phone"></i> Call
              </a>
              <a href="https://wa.me/918431748754" className="contact-btn whatsapp-btn" target="_blank" rel="noopener noreferrer">
                <i className="fa-brands fa-whatsapp"></i> WhatsApp
              </a>
            </div>
          </div>

          {/* Demo Call Card */}
          <div className="contact-card">
            <div className="contact-card-icon">
              <i className="fa-solid fa-calendar"></i>
            </div>
            <h3>Schedule a Demo</h3>
            <p>Book a 30-minute consultation to discuss your needs and see our solutions in action.</p>
            <a
              href="https://calendly.com/aakeeo-technologies/crm-consultations"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-btn demo-btn"
            >
              Request Demo
            </a>
          </div>

          {/* Message Form Card */}
          <div className="contact-card">
            <div className="contact-card-icon">
              <i className="fa-solid fa-message"></i>
            </div>
            <h3>Send Message</h3>
            <p>Tell us about your project requirements and we'll provide a tailored solution.</p>
            <button onClick={openForm} className="contact-btn message-btn">
              Send Message
            </button>
          </div>

          {/* LinkedIn Card */}
          <div className="contact-card">
            <div className="contact-card-icon">
              <i className="fa-brands fa-linkedin"></i>
            </div>
            <h3>Connect on LinkedIn</h3>
            <p>Follow us for industry insights, company updates, and professional networking.</p>
            <a
              href="https://www.linkedin.com/company/aakeeo-technologies"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-btn linkedin-btn"
            >
              Connect with us
            </a>
          </div>

        </div>
      </section>

      {/* Floating Form Modal */}
      {showForm && (
        <div className="form-modal-overlay" onClick={closeForm}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="form-modal-header">
              <h3>Send us a Message</h3>
              <button className="close-btn" onClick={closeForm}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <form className="floating-contact-form" onSubmit={handleSubmit}>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
              <textarea
                name="query"
                placeholder="How can we help you? What are you looking for?"
                rows="5"
                value={formData.query}
                onChange={handleInputChange}
                required
              ></textarea>
              <button
                type="submit"
                className="btn-primary"
                disabled={!validateForm() || isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Contact;